import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useRemoveMember } from '@/hooks/useTeam'
import { Loader2, AlertTriangle } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import type { TeamMember } from '@/hooks/useTeam'

interface RemoveMemberModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  member: TeamMember | null
}

export function RemoveMemberModal({ open, onOpenChange, member }: RemoveMemberModalProps) {
  const { toast } = useToast()
  const removeMutation = useRemoveMember()

  const handleRemove = async () => {
    if (!member) return

    try {
      await removeMutation.mutateAsync(member.id)

      toast({
        title: 'Member removed',
        description: `${member.user.full_name} has been removed from the team`,
      })

      onOpenChange(false)
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to remove member'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  if (!member) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Remove Team Member</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove this member from your team?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Member Info */}
          <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
              {member.user.full_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-medium">{member.user.full_name}</div>
              <div className="text-sm text-muted-foreground">{member.user.email}</div>
            </div>
          </div>

          {/* Warning */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>{member.user.full_name}</strong> will immediately lose access to all knowledge bases and team data.
              This action cannot be undone.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={removeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemove}
            disabled={removeMutation.isPending}
          >
            {removeMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Removing...
              </>
            ) : (
              'Remove Member'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
